import { useState, useEffect } from 'react'
import { CITIES, getPublicCities } from '../config/cities'

interface IpapiResponse {
  city: string
  latitude: number
  longitude: number
}

interface DetectedCityResult {
  city: string
  setCity: (city: string) => void
  isLoading: boolean
  coordinates: { lat: number; lng: number } | null
}

const FALLBACK_CITY = getPublicCities()[0]?.name ?? ''
const CITY_STORAGE_KEY = 'waypo_city'

function getSavedCity(): string | null {
  try {
    const saved = localStorage.getItem(CITY_STORAGE_KEY)
    if (saved && CITIES.find((c) => c.name === saved)) return saved
  } catch {
    // localStorage unavailable (e.g. private browsing restrictions)
  }
  return null
}

// ── Module-level shared state ────────────────────────────────────────────────
// All hook instances share the same city value. Without this, calling setCity
// in one component (e.g. CityPage's city switcher) would not update other
// mounted instances (e.g. the Header nav) until a full page reload.

let _city: string = getSavedCity() ?? FALLBACK_CITY
const _citySubscribers = new Set<(city: string) => void>()

function setSharedCity(newCity: string, persist: boolean): void {
  if (persist) {
    // eslint-disable-next-line no-empty -- localStorage.setItem can throw in private browsing; silently ignore
    try { localStorage.setItem(CITY_STORAGE_KEY, newCity) } catch {}
  }
  _city = newCity
  _citySubscribers.forEach((fn) => fn(newCity))
}

// Deduplicate the GeoIP fetch — one network call regardless of how many
// components have mounted the hook simultaneously.
let _geoIpPromise: Promise<IpapiResponse | null> | null = null

function fetchGeoIp(): Promise<IpapiResponse | null> {
  if (!_geoIpPromise) {
    _geoIpPromise = fetch('https://ipapi.co/json/')
      .then((res) => res.json() as Promise<IpapiResponse>)
      .catch(() => null)
  }
  return _geoIpPromise
}
// ────────────────────────────────────────────────────────────────────────────

/**
 * Detects the user's city via GeoIP (ipapi.co) without requesting browser
 * location permission. Falls back to the first launched city (or the raw
 * detected city if no launched cities are configured yet).
 *
 * If the user has previously selected a city via the city switcher, that
 * choice is persisted in localStorage and takes priority over GeoIP. The
 * selection is also broadcast immediately to all mounted hook instances so
 * the Header nav updates without requiring a page reload.
 */
export function useDetectedCity(): DetectedCityResult {
  const [city, setCityState] = useState<string>(_city)
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null)
  const [isLoading, setIsLoading] = useState(getSavedCity() === null)

  // Subscribe to city changes broadcast from any other component.
  useEffect(() => {
    _citySubscribers.add(setCityState)
    // Catch up in case city changed while this component was unmounted.
    setCityState(_city)
    return () => { _citySubscribers.delete(setCityState) }
  }, [])

  useEffect(() => {
    fetchGeoIp()
      .then((data) => {
        if (!data) return
        if (data.latitude && data.longitude) {
          setCoordinates({ lat: data.latitude, lng: data.longitude })
        }
        // Only apply GeoIP city if the user hasn't explicitly chosen one.
        if (getSavedCity() === null && data.city) {
          const match = getPublicCities().find(
            (c) => c.name.toLowerCase() === data.city.toLowerCase()
          )
          if (match) {
            setSharedCity(match.name, false)
            /* istanbul ignore next -- safety valve for pre-launch when no public cities exist; unreachable in production */
          } else if (getPublicCities().length === 0) {
            setSharedCity(data.city, false)
          }
        }
      })
      .finally(() => setIsLoading(false))
  }, [])

  return {
    city,
    setCity: (newCity: string) => setSharedCity(newCity, true),
    isLoading,
    coordinates,
  }
}
